import React from 'react';
import styled from 'styled-components';
import { FiEdit, FiMessageCircle, FiImage, FiVideo, FiMusic, FiCalendar, FiUsers, FiCheckCircle, FiXCircle, FiClock, FiSend, FiAlertCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import theme from '../../styles/theme';

const CardContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.primary[300]};
  }
`;

const Header = styled.div`
  background: ${props => {
    switch(props.$tipo) {
      case 'image': return `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%)`;
      case 'video': return `linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)`;
      case 'audio': return `linear-gradient(135deg, #f59e0b 0%, #d97706 100%)`;
      default: return `linear-gradient(135deg, ${theme.colors.success[500]} 0%, ${theme.colors.success[600]} 100%)`;
    }
  }};
  padding: ${theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${theme.colors.text.primary};
`;

const IconContainer = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes['2xl']};
  border: 3px solid rgba(255, 255, 255, 0.3);
`;

const CampanhaInfo = styled.div`
  flex: 1;
  margin-left: ${theme.spacing[4]};
`;

const CampanhaNome = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[1]} 0;
`;

const CampanhaTipo = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: ${theme.fontSizes.sm};
  margin: 0;
  text-transform: capitalize;
`;

const ContentSection = styled.div`
  padding: ${theme.spacing[4]};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const DetailIcon = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  min-width: 20px;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${props => {
    switch(props.$status) {
      case 'rascunho': return theme.colors.text.muted;
      case 'agendada': return '#3b82f6';
      case 'enviando': return '#f59e0b';
      case 'enviada': return theme.colors.success[500];
      case 'cancelada': return theme.colors.error[500];
      case 'erro': return '#ef4444';
      default: return theme.colors.text.muted;
    }
  }};
  color: ${theme.colors.text.primary};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[4]};
`;

const ActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${props => props.$primary ? theme.colors.primary[500] : theme.colors.border.primary};
  background: ${props => props.$primary ? theme.colors.primary[500] : 'transparent'};
  color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.background.primary};
    color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.primary[500]};
    border-color: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const formatDate = (dateString) => {
  if (!dateString) return 'Não agendada';
  const date = new Date(dateString);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getTipoIcon = (tipo) => {
  switch(tipo) {
    case 'image': return FiImage;
    case 'video': return FiVideo;
    case 'audio': return FiMusic;
    default: return FiMessageCircle;
  }
};

const getTipoLabel = (tipo) => {
  switch(tipo) {
    case 'image': return 'Imagem';
    case 'video': return 'Vídeo';
    case 'audio': return 'Áudio';
    default: return 'Texto';
  }
};

const getOrigemLabel = (origem) => {
  switch(origem) {
    case 'todos': return 'Todos os contatos';
    case 'favoritos': return 'Contatos favoritos';
    case 'lista_manual': return 'Lista manual';
    default: return origem;
  }
};

const getStatusIcon = (status) => {
  switch(status) {
    case 'rascunho': return FiClock;
    case 'agendada': return FiCalendar;
    case 'enviando': return FiSend;
    case 'enviada': return FiCheckCircle;
    case 'cancelada': return FiXCircle;
    case 'erro': return FiAlertCircle;
    default: return FiClock;
  }
};

const getStatusLabel = (status) => {
  switch(status) {
    case 'rascunho': return 'Rascunho';
    case 'agendada': return 'Agendada';
    case 'enviando': return 'Enviando';
    case 'enviada': return 'Enviada';
    case 'cancelada': return 'Cancelada';
    case 'erro': return 'Erro';
    default: return status;
  }
};

const CampanhaCard = ({ campanha }) => {
  const navigate = useNavigate();
  const TipoIcon = getTipoIcon(campanha.tipo);
  const StatusIcon = getStatusIcon(campanha.status);

  const handleEdit = () => {
    navigate(`/campanhas/${campanha.uuid || campanha.id}/editar`);
  };

  const podeSendaEnviada = ['rascunho', 'agendada', 'erro'].includes(campanha.status);

  return (
    <CardContainer>
      <Header $tipo={campanha.tipo}>
        <IconContainer>
          <TipoIcon />
        </IconContainer>
        <CampanhaInfo>
          <CampanhaNome>{campanha.nome}</CampanhaNome>
          <CampanhaTipo>{getTipoLabel(campanha.tipo)}</CampanhaTipo>
        </CampanhaInfo>
      </Header>

      <ContentSection>
        <Details>
          <DetailItem>
            <DetailIcon><StatusIcon /></DetailIcon>
            <StatusBadge $status={campanha.status}>
              {getStatusLabel(campanha.status)}
            </StatusBadge>
          </DetailItem>
          <DetailItem>
            <DetailIcon><FiUsers /></DetailIcon>
            <span>{getOrigemLabel(campanha.origem_contatos)}</span>
          </DetailItem>
          <DetailItem>
            <DetailIcon><FiCalendar /></DetailIcon>
            <span>{campanha.agendado_para ? formatDate(campanha.agendado_para) : 'Não agendada'}</span>
          </DetailItem>
          {campanha.cliente_nome && (
            <DetailItem>
              <DetailIcon><FiUsers /></DetailIcon>
              <span>{campanha.cliente_nome}</span>
            </DetailItem>
          )}
        </Details>

        <Actions>
          <ActionButton $primary onClick={handleEdit} disabled={!podeSendaEnviada}>
            <ActionIcon><FiEdit /></ActionIcon>
            {podeSendaEnviada ? 'Editar' : 'Visualizar'}
          </ActionButton>
        </Actions>
      </ContentSection>
    </CardContainer>
  );
};

export default CampanhaCard;

